import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {ReportDataCacheService} from '../../services/reportDataCache.service';
import {ReportSelectionService} from '../../services/reportSelection.service';
import {ChartSpinnerHandlerService} from '../../services/chartSpinnerHandler.service';
import {StatisticsApiService} from '../../../services/api/statistics/statistics-api.service';
import {DatePipe} from '@angular/common';
import {HighchartComponent} from '../../../utils/highchart/highchart.component';
import {ChartType} from '../../../utils/highchart/chart-type';
import {ReportFilterType} from '../../enums/report-filter-type.enum';
import {StatisticsTypesList} from '../../enums/statistics.constants';
import {StatisticsElementType} from '../../enums/statistics-element-type.enum';
import * as _ from 'lodash';


@Component({
  selector: 'graph-area-panel',
  templateUrl: './graph-area-panel.component.html',
  styleUrls: ['./graph-area-panel.component.scss']
})
export class GraphAreaPanelComponent implements OnInit {

  constructor(private reportDataCacheService: ReportDataCacheService,
              private reportSelectionService: ReportSelectionService,
              private chartSpinnerHandlerService: ChartSpinnerHandlerService,
              private statisticsApiService: StatisticsApiService,
              private datePipe: DatePipe) {
  }

 // @ViewChild(InnerLoaderComponent, {static: true}) innerLoaderComponent;

  @Input() externalData;

  private selectedItem;

  @Input()
  set selectedReport(selectedReport: any) {
    if (selectedReport === undefined) {
      return;
    }
    this.selectedItem = selectedReport;
    this.reportIdentifier = selectedReport.reportIdentifier;
    this.updateChartTypes(selectedReport);
    this.updateChartData(selectedReport);
    if (this.isChartLoading()) {
      this.startLoadingChart(selectedReport.reportIdentifier);
    }
  }

  @ViewChild(HighchartComponent, {static: false})
  chart: HighchartComponent;

  selectedChartType: any;
  reportIdentifier: any;
  chartOptions: any;
  chartTypes: any;

  ngOnInit(): void {
  }

  onChartTypeClick(type): void {
    const chartType = ChartType[type];

    this.chartOptions.type = chartType.type;
    this.chartOptions.stacked = chartType.stack;
    this.chartOptions.animation = true;

    this.saveChartTypeSelection(type);
    this.chart.setOptions(this.chartOptions);
  }

  updateChartTypeAndParameters(data): void {
    this.updateChartSelectedType(data.type);
    this.updateChartParameters(data, true);
  }


  getChartIcon(chartType): any {
    return ChartType[chartType].icon;
  }

  getChartDisplayName(chartType): any {
    return ChartType[chartType].stack ? 'stacked ' + ChartType[chartType].type : ChartType[chartType].type;
  }

  isChartLoading(): any {
    return this.chartSpinnerHandlerService.isRunning(this.reportIdentifier);
  }

  printChart(): void {
    this.chart.printChart();
  }

  downloadPdf(): void {
    this.chart.downloadChartAsPdf();
  }

  downloadImage(type): void {
    this.chart.downloadChartAsImage(type);
  }

  getSelectedChartIcon(): any {
    if (this.selectedChartType) {
      return ChartType[this.selectedChartType].icon;
    }
    return 'fa fa-line-chart';
  }

  refreshChart(identifier, chartType): void {
    const filters = this.reportDataCacheService.getReportFilters(this.selectedItem);
    this.startLoadingChart(this.reportIdentifier);
    this.statisticsApiService.getReportForChart(filters).then(data => {
      data.type = chartType;
      this.setReportLastUpdateTime(data);
      this.reportDataCacheService.setLastReportData(identifier, data);
      if (identifier === this.reportIdentifier) {
        this.updateChartParameters(data, true);
      }
      this.stopLoadingChart(identifier);
    }).catch(() => {
      this.stopLoadingChart(identifier);
    });
  }

  isRefreshButtonDisabled(): void {
    const filters = this.reportDataCacheService.getReportFilters(this.selectedItem);
    return this.chartSpinnerHandlerService.isRunning(this.reportIdentifier) ||
      this.isEmptyElementsList(this.selectedItem, filters) ||
      this.isEmptyStatisticsList(this.selectedItem, filters);
  }

  private saveChartTypeSelection(type): void {
    const identifier = this.selectedItem.reportIdentifier;
    const data = this.reportDataCacheService.getLastReportData(identifier);

    if (data) {
      data.type = type;
      this.reportDataCacheService.setLastReportData(identifier, data);
    }

    this.updateChartSelectedType(type);
  }

  private updateChartTypes(selectedReport): void {
    this.chartTypes = selectedReport.graphViews.options;
  }

  private updateChartSelectedType(type): void {
    this.selectedChartType = type;
  }

  private updateChartData(report): void {
    const chartData = this.reportDataCacheService.getLastReportData(report.reportIdentifier);
    if (!chartData) {
      this.updateChartSelectedType(report.graphViews.defaultGraphView);
      this.updateChartWithDefaultData(report);
    } else {
      this.updateChartSelectedType(chartData.type);
      this.updateChartParameters(chartData, false);
    }
  }

  private updateChartWithDefaultData(report): void {
    const parameters = this.reportDataCacheService.getReportFilters(report);
    this.updateFilterDefaultElements(report, parameters);
    this.updateFilterDefaultInformation(report, parameters);

    if (this.isEmptyElementsList(report, parameters)) {
      this.updateChartWithEmptySeries(parameters, report.name);
      return;
    }

    if (!this.chartSpinnerHandlerService.isRunning(report.reportIdentifier)) {
      this.startLoadingChart(report.reportIdentifier);
      this.statisticsApiService.getReportForChart(parameters).then((data) => {
        data.type = parameters.chartView;
        this.setReportLastUpdateTime(data);
        this.reportDataCacheService.setLastReportData(report.reportIdentifier, data);
        this.reportDataCacheService.setReportFilters(report.reportIdentifier, parameters);
        if (report.reportIdentifier === this.selectedItem.reportIdentifier) {
          this.updateChartParameters(data, true);
        }
        this.stopLoadingChart(report.reportIdentifier);
      }).catch(() => {
        this.stopLoadingChart(report.reportIdentifier);
      });
    }
  }

  private startLoadingChart(reportIdentifier): void {
    if (this.chart) {
      this.chartSpinnerHandlerService.startLoading(reportIdentifier);
      this.chart.startLoading();
    }
  }

  private stopLoadingChart(reportIdentifier): void {
    if (this.chart) {
      this.chartSpinnerHandlerService.stopLoading(reportIdentifier);
      this.chart.stopLoading();
    }
  }

  private isEmptyElementsList(report, parameters): any {
    return (report.filters.indexOf(ReportFilterType.ELEMENTS) !== -1 && parameters.elements.length === 0) || (
      report.filters.indexOf(ReportFilterType.SINGLE_ELEMENT) !== -1 && !parameters.element);
  }

  private isEmptyStatisticsList(report, parameters): any{
    return report.filters.indexOf(ReportFilterType.MULTIPLE_STATISTICS) !== -1 && parameters.multipleStatistics.length === 0;
  }

  private updateChartWithEmptySeries(parameters, reportName): any {
    const data = {
      type: parameters.chartView,
      series: [],
      title: reportName,
      subTitle: 'By ' + this.getStatisticDisplayName(parameters.singleStatistic)
    };
    this.setReportLastUpdateTime(data);
    this.updateChartParameters(data, true);
  }

  private getStatisticDisplayName(stat): any {
    return StatisticsTypesList.find((option) => {
      return _.isEqual(option.value, stat);
    }).name;
  }

  private updateFilterDefaultElements(report, parameters): void {
    if (report.filters.indexOf(ReportFilterType.ELEMENTS) === -1 &&
      report.filters.indexOf(ReportFilterType.SINGLE_ELEMENT) === -1) {
      return;
    }

    const elementOptions = this.getElementsByElementType(report.elementType, report.reportType);
    if (elementOptions.length !== 0) {
      if (report.filters.indexOf(ReportFilterType.ELEMENTS) !== -1) {
        parameters.elements = [elementOptions[0].id];
      } else if (report.filters.indexOf(ReportFilterType.SINGLE_ELEMENT) !== -1) {
        parameters.element = elementOptions[0].id;
      }

      parameters.elementOptions = elementOptions;
    }
  }

  private updateFilterDefaultInformation(report, parameters): void {
    if (report.reportIdentifier === 'quotaOverTime') {
      parameters.quotaElementType = 'peerConnection';
    }
  }

  private updateChartParameters(parameters, animation): void {
    const chartType = ChartType[this.selectedChartType];
    this.chartOptions = {
      parentType: parameters.parentType,
      type: chartType.type,
      stacked: chartType.stack,
      series: parameters.series,
      title: {text: parameters.title},
      subTitle: parameters.subTitle,
      startDate: parameters.startDate,
      interval: parameters.interval,
      limit: parameters.limit,
      animation,
      exporting: false,
      lastUpdateTime: parameters.lastUpdateTime,
      boost: this.isBoostNeeded(parameters)
    };

 //   this.innerLoaderComponent.showContent();

    setTimeout(() => {
      this.chart.setOptions(this.chartOptions);
    }, 0);
  }

  private setReportLastUpdateTime(data): void {
    data.lastUpdateTime = this.datePipe.transform(new Date(), 'MMM d, y HH:mm');
  }

  private getElementsByElementType(elementType, reportType): any {
    if (elementType !== StatisticsElementType.ARM) {
      return this.getElements(elementType, reportType);
    }
    return [];
  }

  private getElements(elementType, reportType): any {
    if (this.externalData === undefined) {
      return [];
    }
    switch (elementType) {
      case StatisticsElementType.NODE:
        return this.externalData.nodes;
      case StatisticsElementType.ROUTER:
        return this.externalData.routers;
      case StatisticsElementType.PEER_CONNECTION:
        return this.externalData.pCons;
      case StatisticsElementType.CONNECTION:
        return this.externalData.connections;
      case StatisticsElementType.ROUTING_GROUP:
        return this.externalData.routingGroups;
      case StatisticsElementType.ROUTING_RULE:
        return this.externalData.routingRules;
      case StatisticsElementType.RESOURCE_GROUP:
        return this.filterResourceGroup(reportType);
      case StatisticsElementType.CALLS_QUOTA:
        return this.externalData.callsQuotas;
      case StatisticsElementType.CUSTOMER:
        return this.externalData.customers;
      case StatisticsElementType.VOIP_PEER:
        return this.externalData.voipPeers;
    }
  }

  private filterResourceGroup(reportType): any {
    if (reportType === StatisticsElementType.CALLS_QUOTA) {
      return this.externalData.resourceGroups.filter(resourceGroup => resourceGroup.type === 'PEER_CONNECTION');
    }

    return this.externalData.resourceGroup;
  }

  private isBoostNeeded(parameters): boolean {
    if (this.selectedChartType === 'LINE' || this.selectedChartType === 'AREA' || this.selectedChartType === 'STACKED_AREA'){
      if (_.sumBy(parameters.series,  s  => (s as any).data.length) > 8000) {
        parameters.series.forEach((s) => {
          s.data = s.data.map(d => d.y);
        });
        return true;
      }
    }
    return false;
  }

}
